import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { take } from 'rxjs/operators';
import { ApiError, AuthService, MessageService } from '../../../../../core';
import { FunilVendaFaseTipo } from '../../../funil-venda/fase';
import { NegocioQualificacaoSearch } from '../../qualificacao/negocio-qualificacao-search';
import { NegocioQualificacaoCreateHttpService } from '../../qualificacao/services';
import { NegocioOportunidadeSearch } from '../../oportunidade/negocio-oportunidade-search';
import { NegocioOportunidadeCreateHttpService } from '../../oportunidade/services';
import { NegocioFormInsert } from '../negocio-form-insert';
import { NegocioFormInsertStepperComponent } from './steps';
import { CdkStep, StepperSelectionEvent } from '@angular/cdk/stepper';
import { NegocioFormInsertStepFunilComponent, NegocioFormInsertStepLeadComponent, NegocioFormInsertStepClienteComponent, NegocioFormInsertStepNegocioComponent } from './steps';
import { Lead } from '../../../lead/lead';
import { LeadCrudHttpService } from '../../../lead/services/lead-crud-http.service';
import { Cliente } from '../../../../pessoa/cliente/cliente';
import { ClienteCrudHttpService } from '../../../../pessoa/cliente/services';
import { UsuarioRepresentanteHttpService } from '../../../../usuario/services';

@Component({
    selector: 'sgt-negocio-form-insert-wizard',
    templateUrl: './negocio-form-insert-wizard.component.html',
    styleUrls: ['./negocio-form-insert-wizard.component.less'],
})
export class NegocioFormInsertWizardComponent {
    negocio: NegocioFormInsert;

    @Input() isShowBtnClose = true;

    @ViewChild('form') form: NgForm;
    isCreating: boolean;

    // Steps
    @ViewChild('stepper') stepper: NegocioFormInsertStepperComponent;
    // Funil
    @ViewChild('stepFunil') stepFunil: CdkStep;
    @ViewChild('formFunil') formFunil: NegocioFormInsertStepFunilComponent;
    // Lead
    @ViewChild('stepLead') stepLead: CdkStep;
    @ViewChild('formLead') formLead: NegocioFormInsertStepLeadComponent;
    // Cliente
    @ViewChild('stepCliente') stepCliente: CdkStep;
    @ViewChild('formCliente') formCliente: NegocioFormInsertStepClienteComponent;
    // Negócio
    @ViewChild('stepNegocio') stepNegocio: CdkStep;
    @ViewChild('formNegocio') formNegocio: NegocioFormInsertStepNegocioComponent;

    @Output() afterCreateQualificacao = new EventEmitter<NegocioQualificacaoSearch>();
    @Output() afterCreateOportunidade = new EventEmitter<NegocioOportunidadeSearch>();
    @Output() afterClose = new EventEmitter<void>();

    constructor(private authService: AuthService,
                private messageService: MessageService,
                private negocioQualificacaoCreateHttpService: NegocioQualificacaoCreateHttpService,
                private negocioOportunidadeCreateHttpService: NegocioOportunidadeCreateHttpService,
                private leadCrudHttpService: LeadCrudHttpService,
                private clienteCrudHttpService: ClienteCrudHttpService,
                private usuarioRepresentanteHttpService: UsuarioRepresentanteHttpService) { }

    getTitle(): string {
        // Qualificação
        if (this.negocio.isFaseQualificacao()) {
            return 'Nova Qualificação';
        }
        // Oportunidade
        if (this.negocio.isFaseOportunidade()) {
            return 'Nova Oportunidade';
        }
        // Default
        return 'Novo Negócio';
    }

    async insert(idFunilVenda: number, tipoFunilVendaFase: FunilVendaFaseTipo, idLead: number = null, idCliente: number = null) {
        this.negocio = new NegocioFormInsert(idFunilVenda, tipoFunilVendaFase ? tipoFunilVendaFase.id : null, idLead, idCliente);

        // Se informado o lead, carregamos ele
        if (idLead) {
            this.negocio.lead = await this.findLead(idLead);
        }

        // Se informado o cliente, carregamos ele
        if (idCliente) {
            this.negocio.cliente = await this.findCliente(idCliente);
        }

        setTimeout(() => this.stepper.reset(), 0);
    }

    onStepChange(event: StepperSelectionEvent) {
        setTimeout(() => {
            switch (event.selectedStep) {
                case this.stepFunil:
                    //
                    break;
                case this.stepLead:
                    this.formLead?.setFocus();
                    break;
                case this.stepCliente:
                    this.formCliente?.setFocus();
                break;
                case this.stepNegocio:

                break;
            }
        }, 500);
    }

    /** Carrega o lead quando informado */
    private findLead(idLead: number): Promise<Lead> {
        return new Promise<Lead>((resolve, reject) => {
            this.leadCrudHttpService.find(idLead)
                .pipe(
                    take(1)
                )
                .subscribe(
                    lead => resolve(lead),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o lead informado para criar um novo negócio', error);
                        reject(error);
                    }
                );
        });
    }

    /** Carrega o cliente quando informado */
    private findCliente(idCliente: number): Promise<Cliente> {
        return new Promise<Cliente>((resolve, reject) => {
            this.clienteCrudHttpService.find(idCliente)
                .pipe(
                    take(1)
                )
                .subscribe(
                    cliente => resolve(cliente),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o cliente informado para criar um novo negócio', error);
                        reject(error);
                    }
                );
        });
    }

    /** Obtem o id do usuário vinculado ao representante do cliente */
    private getIDUsuarioRepresentanteCliente(idRepresentante: number): Promise<number> {
        return new Promise<number>((resolve, reject) => {
            this.usuarioRepresentanteHttpService.getId(idRepresentante)
                .pipe(
                    take(1)
                )
                .subscribe(
                    id => resolve(id),
                    () => resolve(null)
                );
        });
    }

    /** Avança o step do funil de vendas */
    async nextStepFunilVenda() {
        if (this.negocio.isFaseQualificacao()) {
            // Se não exibir o step do lead e possuir o lead setado, então setamos o responsável do negócio igual a responsável do lead
            if (!this.negocio.isShowLead && this.negocio.lead) {
                this.negocio.idUsuarioResponsavel = this.negocio.lead.idUsuarioResponsavel;
            }
        } else if (this.negocio.isFaseOportunidade()) {
            // Se não exibor o step do cliente e possuir o cliente setado, sentão setamos o responsável do negócio igual ao usuário do representante do cliente
            if (!this.negocio.isShowCliente && this.negocio.cliente) {
                // Obtendo o usuário do representante do cliente
                if (this.negocio.cliente.idRepresentante) {
                    this.negocio.idUsuarioResponsavel = await this.getIDUsuarioRepresentanteCliente(this.negocio.cliente.idRepresentante);
                }

                // Se o representante não possuir um usuário vinculado, então setamos o usuário logado
                if (!this.negocio.idUsuarioResponsavel) {
                    this.negocio.idUsuarioResponsavel = this.authService.getIdUsuario();
                }
            }
        }


        this.stepper.next();
    }

    /** Cria um novo negócio */
    async create() {
        try {
            this.isCreating = true;

            if (this.negocio.isFaseQualificacao()) { // Qualificação
                const qualificacao = await this.createQualificacao();
                this.afterCreateQualificacao.emit(qualificacao);
            } else if (this.negocio.isFaseOportunidade()) { // Oportunidade
                const oportunidade = await this.createOportunidade();
                this.afterCreateOportunidade.emit(oportunidade);
            }

            this.isCreating = false;
        } catch (error) {
            this.messageService.error(error.cause, error.message, error);
            this.isCreating = false;
        }
    }

    /** Cria um novo negócio a ser qualificado */
    private createQualificacao(): Promise<NegocioQualificacaoSearch> {
        return new Promise<NegocioQualificacaoSearch>((resolve, reject) => {
            const qualificacao = this.negocio.createQualificacao();
            this.negocioQualificacaoCreateHttpService.create(qualificacao)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => {
                        this.messageService.success('', 'Qualificação criada com sucesso');
                        resolve(response);
                    },
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Cria uma nova oportunidade de negócio */
    private createOportunidade(): Promise<NegocioOportunidadeSearch> {
        return new Promise<NegocioOportunidadeSearch>((resolve, reject) => {
            const oportunidade = this.negocio.createOportunidade();
            this.negocioOportunidadeCreateHttpService.create(oportunidade)
                .pipe(
                    take(1)
                )
                .subscribe(
                    response => {
                        this.messageService.success('', 'Oportunidade criada com sucesso');
                        resolve(response);
                    },
                    (error: ApiError) => reject(error)
                );
        });
    }

    /** Retornar o texto para o botão de criar um novo negócio */
    getBtnCreateText(): string {
        // Qualificação
        if (this.negocio.isFaseQualificacao()) {
            return 'Criar Qualificação';
        }
        // Oportunidade
        if (this.negocio.isFaseOportunidade()) {
            return 'Criar Oportunidade';
        }
        // Default
        return null;
    }

    /** Retornar o texto para o botão de criando um novo negócio */
    getBtnCreatingText(): string {
        // Qualificação
        if (this.negocio.isFaseQualificacao()) {
            return 'Criando Qualificação';
        }
        // Oportunidade
        if (this.negocio.isFaseOportunidade()) {
            return 'Criando Oportunidade';
        }
        // Default
        return null;
    }

    close() {
        this.negocio = null;
        this.afterClose.emit();
    }
}
